<script setup lang="ts">
import { onBeforeMount, reactive, ref, watch } from 'vue'

import BetaAdvertisementCard from '@/components/beta-dashboard/molecules/BetaAdvertisementCard.vue'
import BetaAdvertisementDialog from '@/components/beta-dashboard/organisms/BetaAdvertisementDialog.vue'
import modal from '@/services/modal'
import notification, { type ToastId } from '@/services/notification'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type {
    BetaAdvertisement,
    BetaAdvertisementInput,
    BetaAdvertisementTitles,
} from '@/types/beta-dashboard'

const betaStore = useBetaDashboardStore()

const dialogOpen = ref(false)
const advertisementForEdit = ref<BetaAdvertisement | null>(null)
const saving = ref(false)
const updatingIds = ref<number[]>([])
const deletingIds = ref<number[]>([])
const titlesOpen = ref(false)
const savingTitles = ref(false)

const titleFields: Array<{
    id: keyof BetaAdvertisementTitles
    label: string
}> = [
    { id: 'menu', label: 'Menu do topo' },
    { id: 'after_content', label: 'Depois do conteúdo' },
    { id: 'footer', label: 'Rodapé' },
    { id: 'sidebar', label: 'Menu lateral' },
]

const titleDraft = reactive<BetaAdvertisementTitles>({
    after_content: '',
    footer: '',
    sidebar: '',
    menu: '',
})

watch(
    () => betaStore.advertisementTitles,
    (titles) => Object.assign(titleDraft, JSON.parse(JSON.stringify(titles))),
    { deep: true, immediate: true },
)

onBeforeMount(() => betaStore.loadAdvertisements())

const openCreate = () => {
    advertisementForEdit.value = null
    dialogOpen.value = true
}

const openEdit = (advertisement: BetaAdvertisement) => {
    advertisementForEdit.value = advertisement
    dialogOpen.value = true
}

const closeDialog = (force = false) => {
    if (saving.value && !force) return
    dialogOpen.value = false
    advertisementForEdit.value = null
}

const saveAdvertisement = async (advertisement: BetaAdvertisementInput) => {
    if (saving.value) return

    saving.value = true
    const editing = Boolean(advertisementForEdit.value?.id)
    const toastId: ToastId = notification.loading(
        editing ? 'Atualizando publicidade…' : 'Adicionando publicidade…',
    )

    try {
        if (advertisementForEdit.value) {
            await betaStore.editAdvertisement({
                ...advertisementForEdit.value,
                ...advertisement,
                id: advertisementForEdit.value.id,
                status: advertisement.status ?? advertisementForEdit.value.status,
            })
        } else {
            await betaStore.addAdvertisement(advertisement)
        }

        notification.update(
            toastId,
            editing ? 'Publicidade atualizada com sucesso.' : 'Publicidade adicionada com sucesso.',
            'success',
        )
        closeDialog(true)
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível salvar a publicidade.',
            'error',
        )
    } finally {
        saving.value = false
    }
}

const toggleAdvertisement = async (advertisement: BetaAdvertisement, status: boolean) => {
    if (updatingIds.value.includes(advertisement.id)) return

    updatingIds.value = [...updatingIds.value, advertisement.id]
    try {
        await betaStore.setAdvertisementStatus(advertisement, status)
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível alterar o status.',
            'error',
        )
    } finally {
        updatingIds.value = updatingIds.value.filter((id) => id !== advertisement.id)
    }
}

const removeAdvertisement = async (advertisement: BetaAdvertisement) => {
    const confirmed = await modal.confirmation(
        'Remover publicidade?',
        `“${advertisement.title}” será excluída permanentemente.`,
        {
            type: 'danger',
            textConfirm: 'Sim, remover',
            textCancel: 'Cancelar',
        },
    )
    if (!confirmed || deletingIds.value.includes(advertisement.id)) return

    deletingIds.value = [...deletingIds.value, advertisement.id]
    const toastId: ToastId = notification.loading('Excluindo publicidade…')
    try {
        await betaStore.removeAdvertisement(advertisement.id)
        notification.update(toastId, 'Publicidade excluída com sucesso.', 'success')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível excluir a publicidade.',
            'error',
        )
    } finally {
        deletingIds.value = deletingIds.value.filter((id) => id !== advertisement.id)
    }
}

const saveTitles = async () => {
    if (savingTitles.value) return

    savingTitles.value = true
    const toastId: ToastId = notification.loading('Salvando títulos…')
    try {
        await betaStore.saveAdvertisementTitles(JSON.parse(JSON.stringify(titleDraft)))
        notification.update(toastId, 'Títulos salvos com sucesso.', 'success')
        titlesOpen.value = false
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível salvar os títulos.',
            'error',
        )
    } finally {
        savingTitles.value = false
    }
}
</script>

<template>
    <div class="beta-advertising">
        <div class="beta-advertising__heading">
            <div>
                <p>Publicidade</p>
                <h2>Banners, textos e códigos</h2>
                <span>Gerencie os conteúdos exibidos nas posições publicitárias do site.</span>
            </div>
            <div class="beta-advertising__heading-actions">
                <button type="button" class="is-secondary" @click="titlesOpen = !titlesOpen">
                    {{ titlesOpen ? 'Fechar títulos' : 'Títulos das posições' }}
                </button>
                <button type="button" class="is-primary" @click="openCreate">+ Nova publicidade</button>
            </div>
        </div>

        <form v-if="titlesOpen" class="beta-advertising__titles" @submit.prevent="saveTitles">
            <div class="beta-advertising__section-heading">
                <div>
                    <h3>Títulos das posições</h3>
                    <p>Personalize o título exibido acima de cada espaço publicitário.</p>
                </div>
            </div>

            <div class="beta-advertising__title-fields">
                <label v-for="field in titleFields" :key="field.id">
                    <span>{{ field.label }}</span>
                    <input v-model="titleDraft[field.id]" type="text" maxlength="120">
                </label>
            </div>

            <div class="beta-advertising__titles-actions">
                <button type="submit" :disabled="savingTitles">
                    {{ savingTitles ? 'Salvando…' : 'Salvar títulos' }}
                </button>
            </div>
        </form>

        <div v-if="betaStore.loadingAdvertisements" class="beta-advertising__state">
            Carregando publicidades…
        </div>

        <div v-else-if="betaStore.advertisementsError" class="beta-advertising__state is-error">
            <span>{{ betaStore.advertisementsError }}</span>
            <button type="button" @click="betaStore.loadAdvertisements()">Tentar novamente</button>
        </div>

        <div v-else-if="!betaStore.advertisements.length" class="beta-advertising__state">
            <strong>Nenhuma publicidade cadastrada</strong>
            <span>Crie um banner, texto ou código incorporado para começar.</span>
            <button type="button" @click="openCreate">Criar publicidade</button>
        </div>

        <div v-else class="beta-advertising__grid">
            <BetaAdvertisementCard
                v-for="advertisement in betaStore.advertisements"
                :key="advertisement.id"
                :advertisement="advertisement"
                :updating="updatingIds.includes(advertisement.id)"
                :deleting="deletingIds.includes(advertisement.id)"
                @edit="openEdit"
                @toggle="toggleAdvertisement"
                @remove="removeAdvertisement"
            />
        </div>

        <BetaAdvertisementDialog
            :open="dialogOpen"
            :advertisement="advertisementForEdit"
            :saving="saving"
            @close="closeDialog"
            @save="saveAdvertisement"
        />
    </div>
</template>

<style scoped>
.beta-advertising {
    display: grid;
    gap: 16px;
}

.beta-advertising__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 17px;
    border-bottom: 1px solid #edf1f6;
}

.beta-advertising__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-advertising__heading h2 {
    margin-top: 3px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-advertising__heading span {
    display: block;
    margin-top: 5px;
    color: #7d8da3;
    font-size: 11px;
}

.beta-advertising__heading-actions {
    display: flex;
    flex: none;
    gap: 7px;
}

.beta-advertising__heading-actions button,
.beta-advertising__titles-actions button,
.beta-advertising__state button {
    min-height: 38px;
    border-radius: var(--beta-button-radius);
    padding: 0 13px;
    font-size: 10px;
    font-weight: 850;
}

.beta-advertising__heading-actions .is-secondary {
    background: #eff3f7;
    color: #60738d;
}

.beta-advertising__heading-actions .is-primary,
.beta-advertising__titles-actions button,
.beta-advertising__state button {
    background: var(--main-color);
    color: #fff;
}

.beta-advertising__titles {
    border: 1px solid #e6ebf2;
    border-radius: 17px;
    background: #f9fbfd;
    padding: 14px;
}

.beta-advertising__section-heading h3 {
    color: #263d5a;
    font-size: 14px;
    font-weight: 850;
}

.beta-advertising__section-heading p {
    margin-top: 3px;
    color: #8190a4;
    font-size: 12px;
}

.beta-advertising__title-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
    margin-top: 11px;
}

.beta-advertising__title-fields label {
    display: grid;
    gap: 5px;
}

.beta-advertising__title-fields span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 850;
}

.beta-advertising__title-fields input {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 11px;
    background: #fff;
    padding: 9px 10px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-advertising__title-fields input:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-advertising__titles-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 11px;
}

.beta-advertising__titles-actions button:disabled {
    cursor: wait;
    opacity: 0.55;
}

.beta-advertising__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.beta-advertising__state {
    display: grid;
    min-height: 180px;
    place-content: center;
    justify-items: center;
    gap: 7px;
    border: 1px dashed #dbe3ed;
    border-radius: 17px;
    background: #f9fbfd;
    padding: 20px;
    color: #7d8da2;
    font-size: 12px;
    text-align: center;
}

.beta-advertising__state strong {
    color: #314965;
    font-size: 14px;
}

.beta-advertising__state button {
    margin-top: 5px;
}

.beta-advertising__state.is-error {
    color: #a34e4e;
}

@media (max-width: 760px) {
    .beta-advertising__heading {
        flex-direction: column;
    }

    .beta-advertising__heading-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .beta-advertising__grid,
    .beta-advertising__title-fields {
        grid-template-columns: 1fr;
    }
}
</style>
